import { jsx } from 'react/jsx-runtime';
import { colors, isPresetColor } from '@flex-design-system/fx-colors';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { forwardRef, useMemo } from 'react';
import { CheckboxProvider, useCheckboxContext } from './context.mjs';
import CheckIcon from './icon/CheckIcon.mjs';
import IndeterminateIcon from './icon/IndeterminateIcon.mjs';
import { StyledLabel, StyledLabelContent, StyledRoot, StyledIndicator } from './styled.mjs';

const CheckboxLabel = forwardRef(
  ({
    children,
    disabled = false,
    size = "default",
    space = 8,
    align = "start",
    ...props
  }, forwardedRef) => {
    return /* @__PURE__ */ jsx(CheckboxProvider, { disabled, size, children: /* @__PURE__ */ jsx(
      StyledLabel,
      {
        ...props,
        size,
        disabled,
        ref: forwardedRef,
        children: /* @__PURE__ */ jsx(StyledLabelContent, { space, align, children })
      }
    ) });
  }
);
const CHECKBOX_ROOT_NAME = "CheckboxRoot";
const CheckboxRoot = forwardRef(function CheckboxRoot2({
  children,
  disabled: disabledFromProps = false,
  checked: checkedFromProps,
  defaultChecked,
  onCheckedChange,
  size: sizeFormProps = "default",
  color,
  ...props
}, forwardedRef) {
  const [checked = false, setChecked] = useControllableState({
    prop: checkedFromProps,
    defaultProp: defaultChecked,
    onChange: onCheckedChange
  });
  const context = useCheckboxContext(CHECKBOX_ROOT_NAME);
  const disabled = context.disabled || disabledFromProps;
  const size = context.size ?? sizeFormProps;
  return /* @__PURE__ */ jsx(
    StyledRoot,
    {
      ...props,
      ref: forwardedRef,
      checked,
      defaultChecked,
      disabled,
      onCheckedChange: setChecked,
      size,
      children: /* @__PURE__ */ jsx(
        CheckboxIndicator,
        {
          state: checked,
          size,
          disabled,
          color
        }
      )
    }
  );
});
const CheckboxIndicator = forwardRef(
  ({ state, color = "primary", ...props }, forwardedRef) => {
    const icon = useMemo(() => {
      if (state === "indeterminate") {
        return /* @__PURE__ */ jsx(IndeterminateIcon, {});
      }
      return state ? /* @__PURE__ */ jsx(CheckIcon, {}) : null;
    }, [state]);
    return /* @__PURE__ */ jsx(
      StyledIndicator,
      {
        ...props,
        ref: forwardedRef,
        forceMount: true,
        css: {
          '&[data-state="checked"], &[data-state="indeterminate"]': {
            backgroundColor: isPresetColor(color) ? colors[color] : color
          }
        },
        children: icon
      }
    );
  }
);
const Checkbox = CheckboxRoot;
Checkbox.Label = CheckboxLabel;

export { Checkbox as default };
//# sourceMappingURL=Checkbox.mjs.map
